"use client";

import { useState, useEffect } from "react";
import {
  X,
  FileText,
  User,
  Briefcase,
  CreditCard,
  Users,
  CheckCircle,
  AlertTriangle,
  Info,
  Calendar,
  Phone,
  Globe,
  Building,
  Shield,
  Heart,
  FileCheck,
  Banknote,
  Car,
  Archive,
} from "lucide-react";
import ZipFileValidation from "./ZipFIleValidation";
import { useTranslation } from "react-i18next";
import totalEmployeesIcon from "../../../../assets/images/icons/Total Employees.svg";

const ValidationModal = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();

  const [activeTab, setActiveTab] = useState("personal");
  const [activeDocumentSubTab, setActiveDocumentSubTab] =
    useState("Salary Details");
  const [citizenStatus, setCitizenStatus] = useState("true"); // Default to citizen for demo

  // Document tabs based on your exact structure
  const allDocsTabs = [
    "Salary Details",
    "Iqama",
    "National ID",
    "Account",
    "Health Insurance",
    "Health Card",
    "Passport",
    "Visa",
    "Contract",
    "Driving License",
    "Subscription",
  ];

  // Filter tabs based on citizen status (matching your logic)
  const getFilteredDocsTabs = () => {
    return allDocsTabs.filter((tab) => {
      if (citizenStatus === "true") {
        return tab !== "Iqama" && tab !== "Visa"; // Hide 'Iqama' and 'Visa' if citizen is true
      } else {
        return tab !== "National ID"; // Hide 'National ID' for non-citizen
      }
    });
  };

  // Reset sub-tab when switching to documents tab or when citizen status changes
  useEffect(() => {
    if (activeTab === "documents") {
      const filteredTabs = getFilteredDocsTabs();
      setActiveDocumentSubTab(filteredTabs[0] || "Salary Details");
    }
  }, [activeTab, citizenStatus]);

  // Personal Information Validations (Step 1)
  const personalValidations = [
    {
      category: "Name Fields",
      icon: <User className="w-4 h-4" />,
      fields: [
        {
          field: "First Name (English)",
          rules: [
            "Required field - cannot be empty",
            "Maximum 30 characters allowed",
            "only English letters, spaces, apostrophes (’), dashes (-), and periods (.) allowed",
            "No special characters or numbers permitted",
          ],
          type: "required",
          example: "Ahmed, John Smith",
        },
        {
          field: "First Name (Arabic)",
          rules: [
            "Required field - cannot be empty",
            "Maximum 30 characters allowed",
            "Only Arabic letters and spaces allowed",
            "No numbers or special characters permitted",
          ],
          type: "required",
          example: "أحمد, محمد علي",
        },
        {
          field: "Second Name (English)",
          rules: [
            "Optional field",
            "Maximum 30 characters if provided",
            "only English letters, spaces, apostrophes (’), dashes (-), and periods (.) allowed",
            "No special characters or numbers permitted",
          ],
          type: "optional",
          example: "Ali, Middle",
        },
        {
          field: "Second Name (Arabic)",
          rules: [
            "Optional field",
            "Maximum 30 characters if provided",
            "Only Arabic letters and spaces allowed",
            "No numbers or special characters permitted",
          ],
          type: "optional",
          example: "علي, عبدالله",
        },
        {
          field: "Third Name (English)",
          rules: [
            "Optional field",
            "Maximum 30 characters if provided",
            "only English letters, spaces, apostrophes (’), dashes (-), and periods (.) allowed",
            "No special characters or numbers permitted",
          ],
          type: "optional",
          example: "Hassan, Third",
        },
        {
          field: "Third Name (Arabic)",
          rules: [
            "Optional field",
            "Maximum 30 characters if provided",
            "Only Arabic letters and spaces allowed",
            "No numbers or special characters permitted",
          ],
          type: "optional",
          example: "حسن, عبدالرحمن",
        },
        {
          field: "Last Name (English)",
          rules: [
            "Required field - cannot be empty",
            "Maximum 30 characters allowed",
            "only English letters, spaces, apostrophes (’), dashes (-), and periods (.) allowed",
            "No special characters or numbers permitted",
          ],
          type: "required",
          example: "Rashid, Johnson",
        },
        {
          field: "Last Name (Arabic)",
          rules: [
            "Required field - cannot be empty",
            "Maximum 30 characters allowed",
            "Only Arabic letters and spaces allowed",
            "No numbers or special characters permitted",
          ],
          type: "required",
          example: "الراشد, العتيبي",
        },
      ],
    },
    {
      category: "Contact Information",
      icon: <Phone className="w-4 h-4" />,
      fields: [
        {
          field: "Phone Number",
          rules: [
            "Required field - cannot be empty",
            "Must be a valid Saudi mobile number",
            "Accepts formats: 00966xxxxxxxxx, 966xxxxxxxxx, +966xxxxxxxxx, 0xxxxxxxxx",
            "Must start with 5 after country code",
            "Exactly 9 digits after the 5",
            "Arabic numerals automatically converted to Western numerals",
          ],
          type: "required",
          example: "0512345678, +966512345678, 966512345678, 565444434",
        },
        {
          field: "Email Address",
          rules: [
            "Required field - cannot be empty",
            "Must be a valid email format",
            "Must contain @ symbol and valid domain",
            "Case-insensitive validation",
          ],
          type: "required",
          example: "user@gmail.com, user@awn.sa",
        },
      ],
    },
    {
      category: "Personal Details",
      icon: <Calendar className="w-4 h-4" />,
      fields: [
        {
          field: "Date of Birth (Hijri)",
          rules: [
            "Required field - cannot be empty",
            "Must be at least 18 years old",
            "Accepts formats: YYYY-MM-DD",
            "Valid Hijri date (month ≤ 12, day ≤ 30)",
          ],
          type: "required",
          example: "24-09-1420, 1420-09-24T00:00:00.000Z",
        },
        {
          field: "Date of Birth (Gregorian)",
          rules: [
            "Required field - cannot be empty",
            "Must be at least 18 years old",
            "Accepts YYYY-MM-DD format ",
            "Cannot be a future date",
          ],
          type: "required",
          example: "1990-05-15, 1985-12-25",
        },
        {
          field: "Religion",
          rules: [
            "Required field - Please select a religion from the available list.",
            "The entered religion must exactly match an active religion in the system.",
            "ensure correct spelling and capitalization.",
          ],
          type: "required",
          example: "Islam, Christianity, Judaism, etc.",
        },
        {
          field: "Marital Status",
          rules: [
            "Required field - must select from options",
            "Valid options: Single, Married",
          ],
          type: "required",
          example: "Single, Married",
        },
        {
          field: "Gender",
          rules: [
            "Required field - must select from options",
            "Valid options: Male, Female",
          ],
          type: "required",
          example: "Male, Female",
        },
      ],
    },
    {
      category: "Citizenship & Identity",
      icon: <Shield className="w-4 h-4" />,
      fields: [
        {
          field: "Citizen Status",
          rules: [
            "Required field - must select Yes or No",
            "Determines which identity documents are required",
            "Affects nationality and ID validation rules",
          ],
          type: "required",
          example: "Yes (Saudi Citizen), No (Non-Saudi)",
        },
        {
          field: "Nationality",
          rules: [
            "Required only if Citizen Status is 'No'",
            "Must select from existing active nationalities",
            "Must be a exact same existing nationality name from the nationality list",
            "Not required for Saudi citizens",
            "ensure correct spelling and capitalization.",
          ],
          type: "conditional",
          example: "Pakistan, Indian, Egyptian, etc.",
        },
        {
          field: "National ID",
          rules: [
            "Required only if Citizen Status is 'Yes'",
            "Must be exactly 10 digits",
            "Only numeric characters allowed",
            "No spaces or special characters",
          ],
          type: "conditional",
          example: "1234567890",
        },
      ],
    },
  ];

  // Job Information Validations (Step 2)
  const jobValidations = [
    {
      category: "Organization Details",
      icon: <Building className="w-4 h-4" />,
      fields: [
        {
          field: "Branch",
          rules: [
            "Required field - Please select a branch from the available list.",
            "The entered branch must exactly match an active branch in the system.",
            "ensure correct spelling and capitalization.",
          ],
          type: "required",
          example: "Main Branch, Riyadh Branch, Jeddah Branch",
        },
        {
          field: "Department",
          rules: [
            "Required field - Please select a department from the available list.",
            "The entered department must exactly match an active department in the system.",
            "ensure correct spelling and capitalization.",
          ],
          type: "required",
          example: "HR, Finance, IT, Operations",
        },
        {
          field: "Role",
          rules: [
            "Required field - Please select a role from the available list.",
            "The entered role must exactly match an active role in the system.",
            "ensure correct spelling and capitalization.",
          ],
          type: "required",
          example: "Manager, Senior Developer, Analyst",
        },
        {
          field: "Job Title",
          rules: [
            "Required field - Please select a job title from the available list.",
            "The entered job title must exactly match an active job title in the system.",
            "ensure correct spelling and capitalization.",
          ],
          type: "required",
          example: "Software Engineer, HR Manager, Accountant",
        },
        {
          field: "Designation",
          rules: [
            "Required field - Please select a designation from the available list.",
            "The entered designation must exactly match an active designation in the system.",
            "ensure correct spelling and capitalization.",
          ],
          type: "required",
          example: "Senior, Junior, Lead, Associate",
        },
        {
          field: "Reporting To",
          rules: [
            "Required field - Please select a employee email from the available employee list.",
            "The entered email must exactly match an active employee's email in the system.",
            "ensure correct spelling and capitalization.",
          ],
          type: "required",
          example: "John Smith (Manager), Ahmed Ali (Team Lead)",
        },
      ],
    },
    {
      category: "Employment Terms",
      icon: <Briefcase className="w-4 h-4" />,
      fields: [
        {
          field: "Joining Date",
          rules: [
            "Required field - cannot be empty",
            "Must be a valid date format",
            "Cannot be a future date beyond reasonable limits",
          ],
          type: "required",
          example: "2024-01-15, 2023-12-01",
        },
        {
          field: "Job Grade",
          rules: ["Required field - cannot be empty"],
          type: "required",
          example: "Grade A, Grade B, Level 1, Level 2",
        },
        {
          field: "Contract Type",
          rules: [
            "Required field - must select from dropdown",
            "Must be an existing contract type in the system",
          ],
          type: "required",
          example: "Limited, Unlimited",
        },
        {
          field: "Probation Period",
          rules: [
            "Required field - cannot be empty",
            "Must specify probation period duration",
            "Must specify in Days",
            "More than 180 is not allowed",
          ],
          type: "required",
          example: "180, 60, 90 etc.",
        },
        {
          field: "Employment Type",
          rules: [
            "Required field - must select from dropdown",
            "Must be an existing employment type in the system",
          ],
          type: "required",
          example: "Full Time, Part Time, Distance",
        },
      ],
    },
  ];

  // Documents & Financial Validations organized by your exact tab structure
  const documentsValidations = {
    "Salary Details": {
      category: "Salary Details",
      icon: <Banknote className="w-4 h-4" />,
      fields: [
        {
          field: "Basic Pay",
          rules: [
            "Required field - cannot be empty",
            "Must be a valid number",
            "Cannot be negative (0 or positive values allowed)",
            "Maximum value: 9,999,999,999",
            "Decimal values allowed",
          ],
          type: "required",
          example: "5000, 7500.50, 10000",
        },
        {
          field: "Housing Allowance",
          rules: [
            "Required field - cannot be empty",
            "Must be a valid number",
            "Cannot be negative (0 or positive values allowed)",
            "Maximum value: 9,999,999,999",
          ],
          type: "required",
          example: "2000, 1500, 0",
        },
        {
          field: "Transport Allowance",
          rules: [
            "Required field - cannot be empty",
            "Must be a valid number",
            "Cannot be negative (0 or positive values allowed)",
            "Maximum value: 9,999,999,999",
          ],
          type: "required",
          example: "500, 800, 0",
        },
        {
          field: "Food Allowance",
          rules: [
            "Required field - cannot be empty",
            "Must be a valid number",
            "Cannot be negative (0 or positive values allowed)",
            "Maximum value: 9,999,999,999",
          ],
          type: "required",
          example: "300, 400, 0",
        },
        {
          field: "Other Allowances",
          rules: [
            "Required field - cannot be empty",
            "Must be a valid number",
            "Cannot be negative (0 or positive values allowed)",
            "Maximum value: 9,999,999,999",
          ],
          type: "required",
          example: "200, 500, 0",
        },
        {
          field: "GOSI Deduction",
          rules: [
            "Required only for Saudi citizens",
            "Must be a valid number if provided",
            "Cannot be negative",
            "Maximum value: 9,999,999,999",
            "Not required for non-citizens",
          ],
          type: "conditional",
          example: "10, 9.75",
        },
      ],
    },
    Iqama: {
      category: "Iqama (Non-Citizens Only)",
      icon: <FileCheck className="w-4 h-4" />,
      fields: [
        {
          field: "Iqama Profession",
          rules: [
            "Required field for non-Saudi citizens",
            "Cannot be empty or contain only spaces",
            "Must be a valid profession description",
          ],
          type: "required",
          example: "Software Engineer, Accountant, Manager",
        },
        {
          field: "Iqama ID",
          rules: [
            "Required field for non-Saudi citizens",
            "Must be exactly 10 digits",
            "Accepts Arabic or Western numerals",
            "No spaces or special characters",
          ],
          type: "required",
          example: "2345678901",
        },
        {
          field: "Iqama ID Expiry",
          rules: [
            "Required field for non-Saudi citizens",
            "Must be a valid date format",
            "Cannot be a past date",
          ],
          type: "required",
          example: "2025-06-30",
        },
        {
          field: "Work Permit Number",
          rules: [
            "Required field for non-Saudi citizens",
            "Must be exactly 10 digits",
            "Accepts Arabic or Western numerals",
            "No spaces or special characters",
          ],
          type: "required",
          example: "3456789012",
        },
        {
          field: "Work Permit Expiry",
          rules: [
            "Required field for non-Saudi citizens",
            "Must be a valid date format",
            "Cannot be a past date",
          ],
          type: "required",
          example: "2025-08-15",
        },
        {
          field: "Iqama Document Front",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "iqama_front.jpg, iqama_front.pdf",
        },
        {
          field: "Iqama Document Back",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "iqama_back.jpg, iqama_back.pdf",
        },
      ],
    },
    "National ID": {
      category: "National ID (Citizens Only)",
      icon: <Shield className="w-4 h-4" />,
      fields: [
        {
          field: "National ID",
          rules: [
            "Required field for Saudi citizens",
            "Must be exactly 10 digits",
            "Only numeric characters allowed",
            "No spaces or special characters",
            "must match with the personal info National ID field",
          ],
          type: "required",
          example: "1234567890",
        },
        {
          field: "National ID Expiry",
          rules: [
            "Required field for Saudi citizens",
            "Must be a valid date format",
            "Cannot be a past date",
          ],
          type: "required",
          example: "2025-12-31",
        },
        {
          field: "Date of Birth",
          rules: [
            "Required field - cannot be empty",
            "Must be at least 18 years old",
            "Accepts YYYY-MM-DD format ",
            "Cannot be a future date",
            "must match with the personal info Date of birth field",
          ],
          type: "required",
          example: "1990-05-15, 1985-12-25",
        },
        {
          field: "Place of birth",
          rules: ["Optional field for Saudi citizens"],
          type: "optional",
          example: "Riyadh, Mumbai",
        },
        {
          field: "National ID Document Front",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "national_id_front.jpg, national_id_front.pdf",
        },
        {
          field: "National ID Document Back",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "national_id_back.jpg, national_id_back.pdf",
        },
      ],
    },
    Account: {
      category: "Banking Account Information",
      icon: <CreditCard className="w-4 h-4" />,
      fields: [
        {
          field: "Bank Name",
          rules: [
            "Required field - Please select a bank from the available list.",
            "The entered bank must exactly match an active bank in the system.",
            "ensure correct spelling and capitalization.",
          ],
          type: "required",
          example: "IIB, SBI, Axis",
        },
        {
          field: "Account Holder Name",
          rules: [
            "Required field - cannot be empty",
            "Maximum 50 characters allowed",
            "Only Arabic letters, English letters, and spaces allowed",
            "No numbers or special characters",
          ],
          type: "required",
          example: "Ahmed Ali, أحمد علي الراشد",
        },
        {
          field: "IBAN Number",
          rules: [
            "Required field - cannot be empty",
            "Minimum 15 characters, maximum 34 characters",
            "Accepts Arabic letters, English letters, and numbers",
            "Cannot contain only letters",
            "Must include at least one number",
          ],
          type: "required",
          example: "SA0380000000608010167519, SA1234567890123456789",
        },
        {
          field: "Confirm IBAN",
          rules: [
            "Required field - cannot be empty",
            "Must exactly match the IBAN Number field",
            "Case-sensitive validation",
          ],
          type: "required",
          example: "Must match IBAN Number exactly",
        },
        {
          field: "IFSC Code",
          rules: [
            "Optional field",
            "If provided, must follow format: 4 letters + 0 + 6 alphanumeric characters",
            "All letters must be uppercase",
            "Fifth character must be 0",
          ],
          type: "optional",
          example: "SBIN0001234, HDFC0000123",
        },
        {
          field: "Account Document Front",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "account_front.jpg, account_front.pdf",
        },
        {
          field: "Account Document Back",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "account_back.jpg, account_back.pdf",
        },
      ],
    },
    "Health Insurance": {
      category: "Health Insurance Information",
      icon: <Heart className="w-4 h-4" />,
      fields: [
        {
          field: "Insurance Class",
          rules: [
            "Required field - cannot be empty",
            "Minimum 2 characters, maximum 30 characters",
            "Must be a valid insurance class description",
          ],
          type: "required",
          example: "Class A, Premium, Basic, Standard",
        },
        {
          field: "Insurance Number",
          rules: [
            "Required field - cannot be empty",
            "Minimum 5 characters, maximum 20 characters",
            "Only Arabic letters, English letters, and numbers allowed",
            "Must include at least one digit",
            "No spaces or special characters",
          ],
          type: "required",
          example: "INS123456, 987XYZ, 448890",
        },
        {
          field: "Insurance Expiry",
          rules: [
            "Required field - cannot be empty",
            "Must be a valid date format",
            "Cannot be a past date",
          ],
          type: "required",
          example: "2025-12-31, 2026-06-30",
        },
        {
          field: "Insurance Document Front",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "insurance_front.jpg, insurance_front.pdf",
        },
        {
          field: "Insurance Document Back",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "insurance_back.jpg, insurance_back.pdf",
        },
      ],
    },
    "Health Card": {
      category: "Health Card Information",
      icon: <Heart className="w-4 h-4" />,
      fields: [
        {
          field: "Health Card Required",
          rules: [
            "Required field - must select Yes or No",
            "Determines if health card details are mandatory",
          ],
          type: "required",
          example: "Yes, No",
        },
        {
          field: "Health Card Number",
          rules: [
            "Required only if Health Card is 'Yes'",
            "Minimum 5 characters, maximum 20 characters",
            "Only Arabic letters, English letters, and numbers allowed",
            "Must include at least one number",
            "Must be empty if Health Card is 'No'",
          ],
          type: "conditional",
          example: "HC123456, صحة789, HEALTH456",
        },
        {
          field: "Health Card Expiry",
          rules: [
            "Required only if Health Card is 'Yes'",
            "Must be a valid date format",
            "Must be empty if Health Card is 'No'",
          ],
          type: "conditional",
          example: "2025-09-30",
        },
        {
          field: "Health Card Document Front",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "health_card_front.jpg, health_card_front.pdf",
        },
        {
          field: "Health Card Document Back",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "health_card_back.jpg, health_card_back.pdf",
        },
      ],
    },
    Passport: {
      category: "Passport Information",
      icon: <Globe className="w-4 h-4" />,
      fields: [
        {
          field: "Passport Number",
          rules: [
            "Required field - cannot be empty",
            "Cannot contain only letters",
            "Must include numbers or alphanumeric combination",
            "No spaces allowed",
          ],
          type: "required",
          example: "A1234567, P123456789, 12345678",
        },
        {
          field: "Passport Expiry",
          rules: [
            "Required field - cannot be empty",
            "Must be a valid date format",
            "Cannot be a past date",
          ],
          type: "required",
          example: "2025-12-31",
        },
        {
          field: "Passport Issue Date",
          rules: [
            "Required field - cannot be empty",
            "Must be a valid date format",
            "Cannot be in the future",
            "Must be before passport expiry date",
          ],
          type: "required",
          example: "2020-01-15",
        },
        {
          field: "Issue Place",
          rules: [
            "Required field - cannot be empty",
            "Cannot contain only numbers",
            "Must be a valid location name",
            "No leading/trailing spaces",
          ],
          type: "required",
          example: "Riyadh, Jeddah, Embassy of Saudi Arabia",
        },
        {
          field: "Passport Document Front",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "passport_front.jpg, passport_front.pdf",
        },
        {
          field: "Passport Document Back",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "passport_back.jpg, passport_back.pdf",
        },
      ],
    },
    Visa: {
      category: "Visa Information (Non-Citizens Only)",
      icon: <Globe className="w-4 h-4" />,
      fields: [
        {
          field: "Visa Number",
          rules: [
            "Required field for non-Saudi citizens",
            "Cannot contain only letters",
            "Must include numbers",
            "No spaces allowed",
          ],
          type: "required",
          example: "V123456, VISA789012",
        },
        {
          field: "Visa Expiry",
          rules: [
            "Required field for non-Saudi citizens",
            "Must be a valid date format",
            "Cannot be a past date",
          ],
          type: "required",
          example: "2025-11-30",
        },
        {
          field: "Visa Issue Date",
          rules: [
            "Required field for non-Saudi citizens",
            "Must be a valid date format",
            "Cannot be in the future",
          ],
          type: "required",
          example: "2023-05-15",
        },
        {
          field: "Visa Issue Place",
          rules: [
            "Required field for non-Saudi citizens",
            "Cannot contain only numbers",
            "Must be a valid location name",
          ],
          type: "required",
          example: "Saudi Embassy, Consulate General",
        },
        {
          field: "Visa Type",
          rules: [
            "Required field for non-Saudi citizens",
            "Must select from existing active visa types",
            "Must be a exact same existing visa type name from the visa type list",
          ],
          type: "required",
          example:
            "business, iqama visa, student, transit, diplomatic, diplomatic, medical",
        },
        {
          field: "Visa Document Front",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "visa_front.jpg, visa_front.pdf",
        },
        {
          field: "Visa Document Back",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "visa_back.jpg, visa_back.pdf",
        },
      ],
    },
    Contract: {
      category: "Contract Information",
      icon: <FileText className="w-4 h-4" />,
      fields: [
        {
          field: "Contract Number",
          rules: [
            "Required field - cannot be empty",
            "Cannot contain only letters",
            "Must include numbers or alphanumeric combination",
          ],
          type: "required",
          example: "CON123456, CONTRACT789, CNT-2024-0467",
        },
        {
          field: "Contract End Date",
          rules: [
            "Required field - cannot be empty",
            "Must be a valid date format",
            "Should be in the future",
          ],
          type: "required",
          example: "2025-12-31",
        },
        {
          field: "Contract Document Front",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "contract_front.jpg, contract_front.pdf",
        },
        {
          field: "Contract Document Back",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "contract_back.jpg, contract_back.pdf",
        },
      ],
    },
    "Driving License": {
      category: "Driving License Information",
      icon: <Car className="w-4 h-4" />,
      fields: [
        {
          field: "Driving License Number",
          rules: [
            "Required field - cannot be empty",
            "Must include at least 1 number",
            "Minimum 4 characters, maximum 25 characters",
            "Can include hyphens but not consecutive (--)",
            "Cannot start or end with hyphen",
            "Only letters, numbers, and hyphens allowed",
          ],
          type: "required",
          example: "ABC123, DL-4567, 123456789, SA-DL123",
        },
        {
          field: "License Validity",
          rules: [
            "Required field - cannot be empty",
            "Must be a valid date format",
            "Cannot be a past date",
          ],
          type: "required",
          example: "2025-08-30",
        },
        {
          field: "Driving License Document Front",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "driving_license_front.jpg, driving_license_front.pdf",
        },
        {
          field: "Driving License Document Back",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "driving_license_back.jpg, driving_license_back.pdf",
        },
      ],
    },
    Subscription: {
      category: "Government Subscriptions",
      icon: <Shield className="w-4 h-4" />,
      fields: [
        {
          field: "GOSI Registration",
          rules: [
            "Required field - must select Yes or No",
            "Determines if GOSI number is required",
          ],
          type: "required",
          example: "Yes, No",
        },
        {
          field: "GOSI Number",
          rules: [
            "Required only if GOSI Registration is 'Yes'",
            "Only numeric characters (0-9) allowed",
            "Minimum 4 digits, maximum 20 digits",
            "Must be empty if GOSI Registration is 'No'",
          ],
          type: "conditional",
          example: "1234567890, 987654321, GD-2024-497378",
        },
        {
          field: "Mudad Registration",
          rules: [
            "Required field - must select Yes or No",
            "Determines if Mudad number is required",
          ],
          type: "required",
          example: "Yes, No",
        },
        {
          field: "Mudad Number",
          rules: [
            "Required only if Mudad Registration is 'Yes'",
            "Only numeric characters (0-9) allowed",
            "Minimum 4 digits, maximum 20 digits",
            "Must be empty if Mudad Registration is 'No'",
          ],
          type: "conditional",
          example: "2345678901, 876543210, MD-2024-497378",
        },
        {
          field: "GOSI Document Front",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "gosi_front.jpg, gosi_front.pdf",
        },
        {
          field: "GOSI Document Back",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "gosi_back.jpg, gosi_back.pdf",
        },
        {
          field: "Subscription Document Front",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "subscription_front.jpg, subscription_front.pdf",
        },
        {
          field: "Subscription Document Back",
          rules: [
            "Optional field",
            "Only images (JPG, PNG) and PDF files are allowed",
            "Maximum file size: 5MB",
          ],
          type: "optional",
          example: "subscription_back.jpg, subscription_back.pdf",
        },
      ],
    },
  };

  // Family Members Validations (Step 4)
  const familyValidations = [
    {
      category: "Dependents Details",
      icon: <Users className="w-4 h-4" />,
      fields: [
        {
          field: "First Name",
          rules: [
            "Required field - cannot be empty",
            "Maximum 30 characters allowed",
            "Only Arabic letters, English letters, and spaces allowed",
            "No special characters or numbers permitted",
            "No leading/trailing spaces",
          ],
          type: "required",
          example: "Ahmed, محمد, Sarah",
        },
        {
          field: "Second Name",
          rules: [
            "Optional field",
            "Maximum 30 characters if provided",
            "Only Arabic letters, English letters, and spaces allowed",
            "No special characters or numbers permitted",
          ],
          type: "optional",
          example: "Ali, عبدالله, Marie",
        },
        {
          field: "Third Name",
          rules: [
            "Optional field",
            "Maximum 30 characters if provided",
            "Only Arabic letters, English letters, and spaces allowed",
            "No special characters or numbers permitted",
          ],
          type: "optional",
          example: "Hassan, حسن, Elizabeth",
        },
        {
          field: "Fourth Name (Last Name)",
          rules: [
            "Required field - cannot be empty",
            "Maximum 30 characters allowed",
            "Only Arabic letters, English letters, and spaces allowed",
            "No special characters or numbers permitted",
          ],
          type: "required",
          example: "الراشد, Johnson",
        },
        {
          field: "ID Expiry",
          rules: [
            "Optional field",
            "Must be a valid date format if provided",
            "Must be in the future if provided",
            "Cannot be empty string - use null if not provided",
          ],
          type: "optional",
          example: "2025-12-31",
        },
        {
          field: "Passport Number",
          rules: [
            "Optional field",
            "Maximum 20 characters if provided",
            "Cannot contain only letters",
            "Must include numbers if provided",
          ],
          type: "optional",
          example: "A1234567, P123456789",
        },
        {
          field: "Passport Expiry",
          rules: [
            "Optional field",
            "Must be a valid date format if provided",
            "Must be in the future if provided",
          ],
          type: "optional",
          example: "2025-08-15",
        },
        {
          field: "Contact Number",
          rules: [
            "Optional field",
            "If provided, must be a valid Saudi mobile number",
            "Accepts formats: 00966xxxxxxxxx, 966xxxxxxxxx, +966xxxxxxxxx, 0xxxxxxxxx",
            "Must start with 5 after country code",
            "Arabic numerals automatically converted",
          ],
          type: "optional",
          example: "0512345678, +966512345678, 512345678",
        },
        {
          field: "Date of Birth",
          rules: [
            "Optional field",
            "Must be a valid date format if provided",
            "Cannot be in the future",
            "Must be realistic (age ≤ 120 years)",
          ],
          type: "optional",
          example: "1990-05-15, 2010-12-25",
        },
        {
          field: "Gender",
          rules: [
            "Optional field",
            "Valid options: Male, Female, or empty",
            "Case-sensitive validation",
          ],
          type: "optional",
          example: "Male, Female",
        },
        {
          field: "Citizen Status",
          rules: ["Optional field", "Valid options: Yes or No"],
          type: "optional",
          example: "Yes, No",
        },
        {
          field: "Nationality",
          rules: [
            "Must select from existing active nationalities",
            "Must be a exact same existing nationality name from the nationality list",
            "ensure correct spelling and capitalization.",
          ],
          type: "optional",
          example: "Saudi, Pakistan, Indian",
        },
        {
          field: "Sponsor Name",
          rules: [
            "Optional field",
            "Maximum 50 characters if provided",
            "Only Arabic letters, English letters, and spaces allowed",
            "No special characters or numbers permitted",
          ],
          type: "optional",
          example: "Ahmed ",
        },
        {
          field: "Sponsor ID",
          rules: [
            "Optional field",
            "Maximum 30 characters if provided",
            "Must be alphanumeric",
            "No letters or special characters",
          ],
          type: "optional",
          example: "1234567890 , SP83436234, SP-2373244",
        },
        {
          field: "Religion",
          rules: [
            "Optional field - Please select a religion from the available list.",
            "The entered religion must exactly match an active religion in the system.",
            "ensure correct spelling and capitalization.",
          ],
          type: "optional",
          example: "Islam, Christianity, Judaism",
        },
        {
          field: "Marital Status",
          rules: [
            "Optional field",
            "Valid options: Married, Single",
            "Case-sensitive validation",
          ],
          type: "optional",
          example: "Married, Single",
        },
        {
          field: "Gender",
          rules: [
            "Required field - must select from options",
            "Valid options: Male, Female",
          ],
          type: "required",
          example: "Male, Female",
        },

        {
          field: "Relation",
          rules: [
            "Optional field - must select from available options.",
            "Valid options  Father, Mother, Brother, Sister, Son, Daughter, Husband, Wife, Uncle, Aunt, Nephew, Niece, Cousin, Grandfather, Grandmother, Grandson, Granddaughter.",
          ],
          type: "optional",
          example: "Father, Mother, Brother",
        },
      ],
    },
  ];

  const getIconForType = (type) => {
    switch (type) {
      case "required":
        return <CheckCircle className="w-4 h-4 text-red-600" />;
      case "conditional":
        return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      case "optional":
        return <Info className="w-4 h-4 text-blue-600" />;
      default:
        return <Info className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "required":
        return "bg-red-100 text-red-700";
      case "conditional":
        return "bg-orange-100 text-orange-700";
      case "optional":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const renderValidationSection = (validations) => (
    <div className="space-y-6">
      {validations.map((category, categoryIndex) => (
        <div
          key={categoryIndex}
          className="border border-gray-200 rounded-lg overflow-hidden"
        >
          <div className="bg-gradient-to-r from-slate-50 to-gray-100 border-b border-gray-200 p-4">
            <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
              {category.icon}
              {t(category.category)}
            </h3>
          </div>
          <div className="p-4 space-y-4">
            {category.fields.map((field, fieldIndex) => (
              <div
                key={fieldIndex}
                className="border border-gray-100 rounded-lg p-4 hover:shadow-sm transition-shadow duration-200"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1.5 rounded-full bg-gray-100">
                    {getIconForType(field.type)}
                  </div>
                  <h4 className="font-semibold text-sm text-gray-900">
                    {t(field.field)}
                  </h4>
                  <span
                    className={`px-2 py-1 text-xs rounded-full font-medium ${getTypeColor(
                      field.type
                    )}`}
                  >
                    {t(field.type)}
                  </span>
                </div>
                <ul className="space-y-1 mb-3">
                  {field.rules.map((rule, ruleIndex) => (
                    <li
                      key={ruleIndex}
                      className="flex items-start gap-2 text-sm text-gray-700"
                    >
                      <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full mt-2 flex-shrink-0"></div>
                      {t(rule)}
                    </li>
                  ))}
                </ul>
                {field.example && (
                  <div className="bg-gray-50 border border-gray-200 rounded p-2">
                    <span className="text-xs font-medium text-gray-600">
                      {t("Examples:")}{" "}
                    </span>
                    <span className="text-xs text-gray-800 font-mono">
                      {field.example}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderDocumentSubTabs = () => {
    const filteredTabs = getFilteredDocsTabs();

    const getSubTabIcon = (tabName) => {
      const iconMap = {
        "Salary Details": <Banknote className="w-4 h-4" />,
        Iqama: <FileCheck className="w-4 h-4" />,
        "National ID": <Shield className="w-4 h-4" />,
        Account: <CreditCard className="w-4 h-4" />,
        "Health Insurance": <Heart className="w-4 h-4" />,
        "Health Card": <Heart className="w-4 h-4" />,
        Passport: <Globe className="w-4 h-4" />,
        Visa: <Globe className="w-4 h-4" />,
        Contract: <FileText className="w-4 h-4" />,
        "Driving License": <Car className="w-4 h-4" />,
        Subscription: <Shield className="w-4 h-4" />,
      };
      return iconMap[tabName] || <FileText className="w-4 h-4" />;
    };

    return (
      <div className="space-y-6">
        {/* Citizen Status Toggle for Demo */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900 text-sm">
                {t("Demo: Citizen Status")}
              </h3>
              <p className="text-blue-700 text-xs">
                {t("Toggle to see how tabs change based on citizenship")}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`text-xs ${
                  citizenStatus === "false"
                    ? "font-semibold text-blue-900"
                    : "text-blue-600"
                }`}
              >
                {t("Non-Citizen")}
              </span>
              <button
                onClick={() =>
                  setCitizenStatus(citizenStatus === "true" ? "false" : "true")
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  citizenStatus === "true" ? "bg-slate-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    citizenStatus === "true" ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
              <span
                className={`text-xs ${
                  citizenStatus === "true"
                    ? "font-semibold text-blue-900"
                    : "text-blue-600"
                }`}
              >
                {t("Citizen")}
              </span>
            </div>
          </div>
        </div>

        {/* Sub-tabs Navigation */}
        <div className="border-b border-gray-200 bg-gray-50 rounded-lg overflow-hidden">
          <div className="flex overflow-x-auto">
            {filteredTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveDocumentSubTab(tab)}
                className={`flex items-center gap-2 px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors duration-200 border-b-2 ${
                  activeDocumentSubTab === tab
                    ? "text-slate-700 border-slate-700 bg-white"
                    : "text-gray-600 hover:text-gray-700 border-transparent hover:bg-gray-100"
                }`}
              >
                {getSubTabIcon(tab)}
                {t(tab)}
              </button>
            ))}
          </div>
        </div>

        {/* Sub-tab Content */}
        {renderDocumentSubTabContent()}
      </div>
    );
  };

  const renderDocumentSubTabContent = () => {
    const currentTabData = documentsValidations[activeDocumentSubTab];
    if (!currentTabData) return null;

    return renderValidationSection([currentTabData]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/30 bg-opacity-40 backdrop-blur-sm">
      <div className="relative w-full max-w-6xl max-h-[95vh] mx-4 bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 bg-gradient-to-r from-slate-700 to-slate-800 text-white">
          <div className="flex items-center gap-3">
            <img
              src={totalEmployeesIcon || "/placeholder.svg"}
              alt="employees icon"
            />
            <div>
              <h2 className="text-xl font-bold">
                {t("Employee Import Validation Rules")}
              </h2>
              <p className="text-indigo-100 text-sm font-medium">
                {t(
                  "Comprehensive validation guidelines for employee data import"
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors duration-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
          <button
            onClick={() => setActiveTab("personal")}
            className={`flex items-center gap-2 px-6 py-4 font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
              activeTab === "personal"
                ? "text-slate-700 border-b-2 border-slate-700 bg-white"
                : "text-gray-600 hover:text-gray-700"
            }`}
          >
            <User className="w-4 h-4" />
            {t("Personal Info")}
          </button>
          <button
            onClick={() => setActiveTab("job")}
            className={`flex items-center gap-2 px-6 py-4 font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
              activeTab === "job"
                ? "text-slate-700 border-b-2 border-slate-700 bg-white"
                : "text-gray-600 hover:text-gray-700"
            }`}
          >
            <Briefcase className="w-4 h-4" />
            {t("Employment Info")}
          </button>
          <button
            onClick={() => setActiveTab("documents")}
            className={`flex items-center gap-2 px-6 py-4 font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
              activeTab === "documents"
                ? "text-slate-700 border-b-2 border-slate-700 bg-white"
                : "text-gray-600 hover:text-gray-700"
            }`}
          >
            <FileCheck className="w-4 h-4" />
            {t("Salary & Documents")}
          </button>
          <button
            onClick={() => setActiveTab("family")}
            className={`flex items-center gap-2 px-6 py-4 font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
              activeTab === "family"
                ? "text-slate-700 border-b-2 border-slate-700 bg-white"
                : "text-gray-600 hover:text-gray-700"
            }`}
          >
            <Users className="w-4 h-4" />
            {t("Dependents")}
          </button>
          <button
            onClick={() => setActiveTab("zipfile")}
            className={`flex items-center gap-2 px-6 py-4 font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
              activeTab === "zipfile"
                ? "text-slate-700 border-b-2 border-slate-700 bg-white"
                : "text-gray-600 hover:text-gray-700"
            }`}
          >
            <Archive className="w-4 h-4" />
            {t("Zip File Upload")}
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {activeTab === "personal" &&
            renderValidationSection(personalValidations)}
          {activeTab === "job" && renderValidationSection(jobValidations)}
          {activeTab === "documents" && renderDocumentSubTabs()}
          {activeTab === "family" && renderValidationSection(familyValidations)}
          {activeTab === "zipfile" && <ZipFileValidation />}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 bg-gray-50 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{t("Legend:")}</span>
            <span className="ml-2 inline-flex items-center gap-1">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-xs">{t("Required")}</span>
            </span>
            <span className="ml-3 inline-flex items-center gap-1">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span className="text-xs">{t("Conditional")}</span>
            </span>
            <span className="ml-3 inline-flex items-center gap-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-xs">{t("Optional")}</span>
            </span>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-700 text-white font-medium text-sm rounded-lg hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-colors duration-200"
          >
            {t("Got it!")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ValidationModal;
